import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { authService } from '../../services/apiService';

const AccountContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 2rem 1rem;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const AccountCard = styled.div`
  background: white;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 750px;
  overflow: hidden;
  border: none;
  transition: box-shadow 0.4s ease, transform 0.4s ease;

  &:hover {
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.2);
    transform: translateY(-4px);
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 3.5rem 2.5rem;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
    background-size: 20px 20px;
  }

  h1 {
    margin: 0 0 0.5rem 0;
    font-size: 2.2rem;
    font-weight: 800;
    position: relative;
    z-index: 1;
  }

  p {
    margin: 0;
    opacity: 0.95;
    font-size: 1rem;
    font-weight: 500;
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 2.5rem 1.5rem;
    h1 {
      font-size: 1.8rem;
    }
  }
`;

const ProfileSection = styled.div`
  padding: 2.5rem;
  max-height: 70vh;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 2rem;
  color: #667eea;
  font-weight: 600;
`;

const ProfileField = styled.div`
  margin-bottom: 1.75rem;
  padding-bottom: 1.75rem;
  border-bottom: 1px solid #ecf0f1;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .label {
    font-size: 0.8rem;
    color: #667eea;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin-bottom: 0.75rem;
  }

  .value {
    font-size: 1.05rem;
    color: #2c3e50;
    font-weight: 600;
    word-break: break-word;
    line-height: 1.6;
  }
`;

const SectionTitle = styled.h2`
  color: #2c3e50;
  font-size: 1.4rem;
  margin-top: 2.5rem;
  margin-bottom: 1.75rem;
  padding-bottom: 1.25rem;
  border-bottom: 2px solid #667eea;
  font-weight: 800;
  letter-spacing: -0.3px;
  
  &:first-child {
    margin-top: 0;
  }
`;

const RoleBadge = styled.div`
  display: inline-block;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 0.65rem 1.75rem;
  border-radius: 30px;
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-top: 1.5rem;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 0.85rem 2.25rem;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.6px;

  &.primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 10px 30px rgba(102, 126, 234, 0.6);
    }
  }

  &.secondary {
    background: white;
    color: #667eea;
    border: 2px solid #667eea;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);

    &:hover {
      background: #f8f9ff;
      transform: translateY(-4px);
      box-shadow: 0 6px 16px rgba(102, 126, 234, 0.25);
    }
  }

  &:active {
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const WarningMessage = styled.div`
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f4f8 100%);
  border: 2px solid #667eea;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.75rem;
  color: #667eea;
  font-size: 0.95rem;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);

  strong {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 700;
  }
`;

const AccountPage = ({ onLogout, userRole, userId }) => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const id = userId || localStorage.getItem('userId');
        console.log('📋 [Account] Fetching profile for user:', id);
        
        const profile = await authService.getUserProfile(id);
        console.log('✅ [Account] Profile fetched:', profile);
        setProfileData(profile);
        setError(null);
      } catch (err) {
        console.error('❌ [Account] Error fetching profile:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId]);

  const handleEditProfile = () => {
    alert('Profile editing feature coming soon!');
  };

  const handleChangePassword = () => {
    alert('Password change feature coming soon!');
  };

  const RoleEmoji = ({ role }) => {
    const emojis = {
      'Buyer': '🛍️',
      'Seller': '📦',
      'Admin': '⚙️',
    };
    return <span style={{ marginRight: '0.5rem' }}>{emojis[role] || '👤'}</span>;
  };

  return (
    <AccountContainer>
      <AccountCard>
        <Header>
          <h1>👤 My Account</h1>
          <p>View your profile information</p>
        </Header>

        <ProfileSection>
          <WarningMessage>
            <strong>ℹ️ Profile Information</strong>
            All your account details are displayed below.
          </WarningMessage>

          {loading ? (
            <LoadingSpinner>Loading profile...</LoadingSpinner>
          ) : error ? (
            <LoadingSpinner style={{ color: '#e74c3c' }}>Error: {error}</LoadingSpinner>
          ) : profileData ? (
            <>
              {/* Basic User Info */}
              <SectionTitle>Basic Information</SectionTitle>
              
              <ProfileField>
                <div className="label">Account Type</div>
                <div className="value">
                  <RoleEmoji role={profileData.role} /> {profileData.role || 'User'}
                </div>
              </ProfileField>

              {/* Seller Details */}
              {profileData.sellerProfile && (
                <>
                  <SectionTitle>🌾 Seller Information</SectionTitle>
                  
                  <ProfileField>
                    <div className="label">Seller Name</div>
                    <div className="value">{profileData.sellerProfile.sellerName || 'N/A'}</div>
                  </ProfileField>

                  <ProfileField>
                    <div className="label">Email</div>
                    <div className="value">{profileData.sellerProfile.email || 'N/A'}</div>
                  </ProfileField>

                  <ProfileField>
                    <div className="label">Phone Number</div>
                    <div className="value">{profileData.sellerProfile.phoneNumber || 'N/A'}</div>
                  </ProfileField>

                  <ProfileField>
                    <div className="label">District</div>
                    <div className="value">{profileData.sellerProfile.district || 'N/A'}</div>
                  </ProfileField>

                  <ProfileField>
                    <div className="label">RTC Document</div>
                    <div className="value">
                      {profileData.sellerProfile.rtcDocumentUrl ? (
                        <a href={profileData.sellerProfile.rtcDocumentUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#667eea', textDecoration: 'underline' }}>
                          View Document
                        </a>
                      ) : (
                        'Not provided'
                      )}
                    </div>
                  </ProfileField>
                </>
              )}

              {/* Buyer Details */}
              {profileData.buyerProfile && (
                <>
                  <SectionTitle>🛒 Buyer Information</SectionTitle>
                  
                  <ProfileField>
                    <div className="label">Buyer Name</div>
                    <div className="value">{profileData.buyerProfile.buyerName || 'N/A'}</div>
                  </ProfileField>

                  <ProfileField>
                    <div className="label">Organization Name</div>
                    <div className="value">{profileData.buyerProfile.organizationName || 'N/A'}</div>
                  </ProfileField>

                  <ProfileField>
                    <div className="label">Email</div>
                    <div className="value">{profileData.buyerProfile.email || 'N/A'}</div>
                  </ProfileField>

                  <ProfileField>
                    <div className="label">Phone Number</div>
                    <div className="value">{profileData.buyerProfile.phoneNumber || 'N/A'}</div>
                  </ProfileField>

                  <ProfileField>
                    <div className="label">Address Line 1</div>
                    <div className="value">{profileData.buyerProfile.addressLine1 || 'N/A'}</div>
                  </ProfileField>

                  <ProfileField>
                    <div className="label">Address Line 2</div>
                    <div className="value">{profileData.buyerProfile.addressLine2 || 'N/A'}</div>
                  </ProfileField>

                  <ProfileField>
                    <div className="label">District</div>
                    <div className="value">{profileData.buyerProfile.district || 'N/A'}</div>
                  </ProfileField>

                  <ProfileField>
                    <div className="label">Pincode</div>
                    <div className="value">{profileData.buyerProfile.pincode || 'N/A'}</div>
                  </ProfileField>

                  <ProfileField>
                    <div className="label">PAN</div>
                    <div className="value">{profileData.buyerProfile.pan || 'N/A'}</div>
                  </ProfileField>

                  <ProfileField>
                    <div className="label">GST</div>
                    <div className="value">{profileData.buyerProfile.gst || 'N/A'}</div>
                  </ProfileField>

                  <ProfileField>
                    <div className="label">PAN Document</div>
                    <div className="value">
                      {profileData.buyerProfile.panUrl ? (
                        <a href={profileData.buyerProfile.panUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#667eea', textDecoration: 'underline' }}>
                          View Document
                        </a>
                      ) : (
                        'Not provided'
                      )}
                    </div>
                  </ProfileField>

                  <ProfileField>
                    <div className="label">GST Document</div>
                    <div className="value">
                      {profileData.buyerProfile.gstUrl ? (
                        <a href={profileData.buyerProfile.gstUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#667eea', textDecoration: 'underline' }}>
                          View Document
                        </a>
                      ) : (
                        'Not provided'
                      )}
                    </div>
                  </ProfileField>
                </>
              )}

              <ButtonGroup>
                <Button className="secondary" onClick={handleEditProfile}>
                  ✏️ Edit Profile
                </Button>
                <Button className="secondary" onClick={handleChangePassword}>
                  🔐 Change Password
                </Button>
                <Button className="primary" onClick={onLogout}>
                  🚪 Logout
                </Button>
              </ButtonGroup>
            </>
          ) : (
            <LoadingSpinner>No profile data available</LoadingSpinner>
          )}
        </ProfileSection>
      </AccountCard>
    </AccountContainer>
  );
};

export default AccountPage;
